import { DEFAULT_LIMIT, DEFAULT_SKIP, LIMIT } from 'Src/consts/table';
import { Count } from 'Src/models/count';
import { superAdminDelete, superAdminGet, superAdminPost, superAdminPut } from './api';
import qs from 'qs';

export interface SuperAdminUsersFilter {
  limit?: number;
  skip?: number;
  order?: string;
  where?: any;
}

export interface SuperAdminUserPermission {
  id: string;
  _id?: string;
  preset?: string;
  status?: string;
  schoolId?: string;
  studentId?: string;
  sportIds?: string[];
  tournamentId?: string;
  priority?: number;
  misId?: string;
  activatedAt?: string;
  deactivatedAt?: string;
  comment?: string;
  details?: any;
  school?: {
    id?: string;
    _id?: string;
    name?: string;
    kind?: string;
  };
  student?: {
    id?: string;
    _id?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  };
  sport?: {
    id?: string;
    _id?: string;
    name?: string;
  };
  sports?: Array<{
    id?: string;
    _id?: string;
    name?: string;
  }>;
  tournament?: {
    id?: string;
    _id?: string;
    name?: string;
  };
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
  isExcludedFromMisIntegration?: boolean;
  sltAccess?: {studentDrilldown: boolean};
}

export interface SuperAdminUserPermissionUpdateData {
  sltAccess?: {studentDrilldown: boolean};
  status?: string;
  activatedAt?: string;
  deactivatedAt?: string;
  sportIds?: string[];
  priority?: number;
  isExcludedFromMisIntegration?: boolean;
}

export interface SuperAdminUserPermissionCreateData extends SuperAdminUserPermissionUpdateData {
  preset: string;
  schoolId?: string;
  studentId?: string;
  comment?: string;
  details?: any;
  tournamentId?: string;
}

export interface SuperAdminUserSession {
  id?: string;
  _id?: string;
  key: string;
  userId?: string;
  role?: string;
  createdAt?: string;
  expireAt?: string;
  isNotExtendable?: boolean;
  lifeTime?: number;
  appDetails?: {
    appName?: string;
    appVersion?: string;
    appPlatform?: string;
  };
}

export type SuperAdminNotificationChannelType =
  | 'IOS'
  | 'ANDROID'
  | 'DEFAULT_EMAIL'
  | 'DEFAULT_SMS'
  | 'MIS_EMAIL'
  | 'MIS_SMS';

export interface SuperAdminUserNotificationChannel {
  id?: string;
  _id?: string;
  userId?: string;
  schoolId?: string;
  token?: string;
  deviceName?: string;
  environment?: string;
  appId?: string;
  type?: SuperAdminNotificationChannelType | string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface SuperAdminUserNotificationChannelCreateData {
  token: string;
  schoolId?: string;
  deviceName?: string;
  environment?: string;
  appId?: string;
  type: SuperAdminNotificationChannelType | string;
}

export interface SuperAdminUserAvailableNotificationChannelSchool {
  id?: string;
  _id?: string;
  schoolId?: string;
  projectName?: string;
  type?: string;
  status?: string;
  [key: string]: any;
}

export interface SuperAdminUser {
  id: string;
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  gender?: string | null;
  birthday?: string | null;
  address?: string;
  avatar?: string;
  status?: string;
  statusNotes?: string;
  blocked?: boolean;
  blockedReason?: string;
  misId?: string;
  misEmail?: string;
  misPhone?: string;
  isPaymentsEnabled?: boolean;
  countOneTimeIosSessions?: number;
  countOneTimeAndroidSessions?: number;
  countOneTimeWebSessions?: number;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
  permissions?: SuperAdminUserPermission[];
  verification?: {
    tokens?: {
      sms?: string;
      email?: string;
      smsSentAt?: string;
      emailSentAt?: string;
    };
    status?: {
      sms?: boolean;
      email?: boolean;
      personal?: boolean;
    };
  };
  notificationChannelsData?: SuperAdminUserNotificationChannel[];
  mergeSchool?: {
    id: string;
    name?: string;
    roles: Array<{
      preset?: string;
      formName?: string;
      houseName?: string;
    }>;
  };
}

export interface SuperAdminUserUpdateData {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  birthday?: string | null;
  gender?: string | null;
  phone?: string;
  address?: string;
  avatar?: string;
  blocked?: boolean;
  notification?: {
    sendNews?: boolean;
    sendInfoUpdates?: boolean;
    sendPromoOffers?: boolean;
  };
  countOneTimeIosSessions?: number;
  countOneTimeAndroidSessions?: number;
  countOneTimeWebSessions?: number;
  isPaymentsEnabled?: boolean;
  misId?: string;
  misEmail?: string;
  misPhone?: string;
}

export interface SuperAdminUserCreateData extends SuperAdminUserUpdateData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  gender: string;
  status?: string;
}

export interface SuperAdminLoginMergePreview {
  allowed: boolean;
  blockers: string[];
  source: SuperAdminUser;
  target: SuperAdminUser;
  transfer: {
    email?: string;
    phone?: string;
    notificationChannels: number;
    targetNotificationChannels: number;
  };
}

export interface SuperAdminLoginMergeResult {
  user: SuperAdminUser;
  channels: { transferred: number; duplicatesRemoved: number };
}

const DEFAULT_FILTER: SuperAdminUsersFilter = {
  limit: LIMIT,
  skip: DEFAULT_SKIP,
  order: 'lastName ASC'
};

const encodedFilterParamsSerializer = (params: any): string =>
  qs.stringify(params, { allowDots: false, encode: true, depth: 20 } as any);

function buildFilter(filter: SuperAdminUsersFilter = DEFAULT_FILTER): SuperAdminUsersFilter {
  return {
    where: filter.where || {},
    skip: typeof filter.skip === 'number' ? filter.skip : DEFAULT_FILTER.skip,
    limit: typeof filter.limit === 'number' ? filter.limit : DEFAULT_FILTER.limit,
    order: filter.order || DEFAULT_FILTER.order
  };
}

export function getSuperAdminUsers(filter: SuperAdminUsersFilter = DEFAULT_FILTER): Promise<SuperAdminUser[]> {
  return superAdminGet<SuperAdminUser[]>('/users', {
    params: {
      filter: buildFilter(filter)
    },
    paramsSerializer: encodedFilterParamsSerializer
  });
}

export function getAllSuperAdminUsers(where?: any): Promise<SuperAdminUser[]> {
  return superAdminGet<SuperAdminUser[]>('/users', {
    params: {
      filter: {
        where: where || {},
        skip: DEFAULT_SKIP,
        limit: DEFAULT_LIMIT,
        order: DEFAULT_FILTER.order
      }
    },
    paramsSerializer: encodedFilterParamsSerializer
  });
}

export function getSuperAdminUsersCount(where?: any): Promise<Count> {
  const config =
    typeof where !== 'undefined'
      ? {
          params: {
            filter: {
              where
            }
          },
          paramsSerializer: encodedFilterParamsSerializer
        }
      : {};

  return superAdminGet<Count>('/users/count', config);
}

export function getSuperAdminUser(userId: string): Promise<SuperAdminUser> {
  return superAdminGet<SuperAdminUser>(`/users/${userId}`);
}

export function getSuperAdminLoginMergeCandidates(
  userId: string,
  search: string,
  schoolId: string
): Promise<SuperAdminUser[]> {
  return superAdminGet<SuperAdminUser[]>(`/users/${userId}/login-merge/candidates`, {
    params: { search, schoolId }
  });
}

export function previewSuperAdminLoginMerge(
  sourceUserId: string,
  targetUserId: string
): Promise<SuperAdminLoginMergePreview> {
  return superAdminPost<SuperAdminLoginMergePreview>(`/users/${sourceUserId}/login-merge/preview`, { targetUserId });
}

export function mergeSuperAdminLoginAccount(
  sourceUserId: string,
  targetUserId: string
): Promise<SuperAdminLoginMergeResult> {
  return superAdminPost<SuperAdminLoginMergeResult>(`/users/${sourceUserId}/login-merge`, { targetUserId });
}

export function getSuperAdminUserAvailableNotificationChannelSchools(
  userId: string
): Promise<SuperAdminUserAvailableNotificationChannelSchool[]> {
  return superAdminGet<SuperAdminUserAvailableNotificationChannelSchool[]>(
    `/users/${userId}/subscriptions/channels/available`
  );
}

export function createSuperAdminUser(data: SuperAdminUserCreateData): Promise<SuperAdminUser> {
  return superAdminPost<SuperAdminUser>('/users', data);
}

export function updateSuperAdminUser(userId: string, data: SuperAdminUserUpdateData): Promise<SuperAdminUser> {
  return superAdminPut<SuperAdminUser>(`/users/${userId}`, data);
}

/**
 * This endpoint changes email and sends a verification email.
 * For silent admin edit, use updateSuperAdminUser(userId, { email }) instead.
 */
export function updateSuperAdminUserEmail(userId: string, email: string): Promise<void> {
  return superAdminPut<void>(`/users/${userId}/email`, { email });
}

/**
 * This endpoint changes phone and sends a verification SMS.
 * For silent admin edit, use updateSuperAdminUser(userId, { phone }) instead.
 */
export function updateSuperAdminUserPhone(userId: string, phone: string): Promise<void> {
  return superAdminPut<void>(`/users/${userId}/phone`, { phone });
}

export function resendSuperAdminUserEmailVerification(userId: string): Promise<{ nextResendAt?: number }> {
  return superAdminPost<{ nextResendAt?: number }>(`/users/${userId}/email/resend`);
}

export function resendSuperAdminUserSmsVerification(userId: string): Promise<{ nextResendAt?: number }> {
  return superAdminPost<{ nextResendAt?: number }>(`/users/${userId}/phone/resend`);
}

export function setSuperAdminUserEmailVerification(userId: string, status: boolean): Promise<SuperAdminUser> {
  return superAdminPut<SuperAdminUser>(`/users/${userId}/verification/status/email`, { status });
}

export function setSuperAdminUserSmsVerification(userId: string, status: boolean): Promise<SuperAdminUser> {
  return superAdminPut<SuperAdminUser>(`/users/${userId}/verification/status/sms`, { status });
}

export function setSuperAdminUserBlock(userId: string, blocked: boolean, blockedReason: string = ''): Promise<void> {
  return superAdminPost<void>(`/users/${userId}/block`, {
    blocked,
    blockedReason
  });
}

export function getSuperAdminUserPermissions(
  userId: string,
  filter: SuperAdminUsersFilter = DEFAULT_FILTER
): Promise<SuperAdminUserPermission[]> {
  return superAdminGet<SuperAdminUserPermission[]>(`/users/${userId}/permissions`, {
    params: {
      filter: buildFilter(filter)
    }
  });
}

export function getAllSuperAdminUserPermissions(userId: string, where?: any): Promise<SuperAdminUserPermission[]> {
  return superAdminGet<SuperAdminUserPermission[]>(`/users/${userId}/permissions`, {
    params: {
      filter: {
        where: where || {},
        skip: DEFAULT_SKIP,
        limit: DEFAULT_LIMIT,
        order: 'createdAt DESC'
      }
    }
  });
}

export function getSuperAdminUserPermissionsCount(userId: string, where?: any): Promise<Count> {
  const config =
    typeof where !== 'undefined'
      ? {
          params: {
            filter: {
              where
            }
          }
        }
      : {};

  return superAdminGet<Count>(`/users/${userId}/permissions/count`, config);
}

export function createSuperAdminUserPermission(
  userId: string,
  data: SuperAdminUserPermissionCreateData
): Promise<SuperAdminUserPermission> {
  return superAdminPost<SuperAdminUserPermission>(`/users/${userId}/permissions`, data);
}

export function updateSuperAdminUserPermission(
  userId: string,
  permissionId: string,
  data: SuperAdminUserPermissionUpdateData
): Promise<SuperAdminUserPermission> {
  return superAdminPut<SuperAdminUserPermission>(`/users/${userId}/permissions/${permissionId}`, data);
}

export function deleteSuperAdminUserPermission(userId: string, permissionId: string): Promise<void> {
  return superAdminDelete<void>(`/users/${userId}/permissions/${permissionId}`);
}

function normalizeSuperAdminUserSession(session: SuperAdminUserSession): SuperAdminUserSession {
  return {
    ...session,
    id: session.id || session._id || session.key
  };
}

export function getSuperAdminUserSessions(
  userId: string,
  filter: SuperAdminUsersFilter = DEFAULT_FILTER
): Promise<SuperAdminUserSession[]> {
  return superAdminGet<SuperAdminUserSession[]>(`/users/${userId}/sessions`, {
    params: {
      filter: buildFilter(filter)
    }
  }).then(sessions => sessions.map(normalizeSuperAdminUserSession));
}

export function getAllSuperAdminUserSessions(userId: string, where?: any): Promise<SuperAdminUserSession[]> {
  return superAdminGet<SuperAdminUserSession[]>(`/users/${userId}/sessions`, {
    params: {
      filter: {
        where: where || {},
        skip: DEFAULT_SKIP,
        limit: DEFAULT_LIMIT,
        order: 'createdAt DESC'
      }
    }
  }).then(sessions => sessions.map(normalizeSuperAdminUserSession));
}

export function getSuperAdminUserSessionsCount(userId: string, where?: any): Promise<Count> {
  const config =
    typeof where !== 'undefined'
      ? {
          params: {
            filter: {
              where
            }
          }
        }
      : {};

  return superAdminGet<Count>(`/users/${userId}/sessionsCount`, config);
}

export function deleteSuperAdminUserSession(userId: string, sessionKey: string): Promise<void> {
  return superAdminDelete<void>(`/users/${userId}/sessions/${sessionKey}`);
}

export function getSuperAdminUserNotificationChannels(
  userId: string,
  filter: SuperAdminUsersFilter = DEFAULT_FILTER
): Promise<SuperAdminUserNotificationChannel[]> {
  return superAdminGet<SuperAdminUserNotificationChannel[]>(`/users/${userId}/notifications/channels`, {
    params: {
      filter: buildFilter(filter)
    }
  });
}

export function createSuperAdminUserNotificationChannel(
  userId: string,
  data: SuperAdminUserNotificationChannelCreateData
): Promise<SuperAdminUserNotificationChannel> {
  return superAdminPost<SuperAdminUserNotificationChannel>(`/users/${userId}/notifications/channels`, data);
}

export function deleteSuperAdminUserNotificationChannel(userId: string, channelId: string): Promise<void> {
  return superAdminDelete<void>(`/users/${userId}/notifications/channels/${channelId}`);
}

export interface SuperAdminUserNotification {
  id?: string;
  _id?: string;
  type?: string;
  entityType?: string;
  title?: string;
  body?: string;
  generatedMessages?: {
    email?: {
      body?: string;
      subject?: string;
    };
    push?: {
      body?: string;
      title?: string;
    };
    sms?: {
      body?: string;
    };
  };
  additionalDescription?: string;
  creatorDetails?: any;
  details?: any;
  factId?: string;
  actionDescriptorId?: string;
  status?: string;
  deliveryStatus?: string;
  channelStatus?: any[];
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export function getSuperAdminUserNotifications(
  userId: string,
  filter: SuperAdminUsersFilter = DEFAULT_FILTER
): Promise<SuperAdminUserNotification[]> {
  return superAdminGet<SuperAdminUserNotification[]>(`/users/${userId}/notifications`, {
    params: {
      filter: buildFilter(filter)
    }
  });
}

export function getAllSuperAdminUserNotifications(userId: string, where?: any): Promise<SuperAdminUserNotification[]> {
  return superAdminGet<SuperAdminUserNotification[]>(`/users/${userId}/notifications`, {
    params: {
      filter: {
        where: where || {},
        skip: DEFAULT_SKIP,
        limit: DEFAULT_LIMIT,
        order: 'createdAt DESC'
      }
    }
  });
}

export function getSuperAdminUserNotificationsCount(userId: string, where?: any): Promise<Count> {
  const config =
    typeof where !== 'undefined'
      ? {
          params: {
            filter: {
              where
            }
          }
        }
      : {};

  return superAdminGet<Count>(`/users/${userId}/notificationsCount`, config);
}
